import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { voiceCloningService } from '../../services/voiceCloning';

function SampleUploader({ cloneId, onSampleUploaded }) {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const acceptedTypes = ['audio/wav', 'audio/mpeg', 'audio/mp3', 'audio/x-wav', 'audio/webm'];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    if (!acceptedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload WAV, MP3 or WebM files.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setIsUploading(true);
    try {
      const result = await voiceCloningService.uploadSample(cloneId, file);
      toast.success('Sample uploaded successfully');
      if (onSampleUploaded) {
        onSampleUploaded(result);
      }
      // Clear any recorded audio
      setAudioBlob(null);
      setAudioUrl(null);
    } catch (error) {
      const message = error.response?.data?.detail || 'Upload failed';
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const startRecording = async () => {
    // Check if mediaDevices is available (requires HTTPS or localhost)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Recording requires HTTPS or localhost. Please use file upload instead.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      
      // Check for supported MIME types
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = ''; // Let browser choose
          }
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const actualMimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      
      // Provide specific error messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Microphone permission denied. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Microphone is in use by another application. Please close other apps using the mic.');
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Microphone does not meet requirements. Please try a different microphone.');
      } else if (error.name === 'SecurityError') {
        toast.error('Recording requires HTTPS. Please use file upload instead.');
      } else {
        toast.error('Failed to access microphone. Please use file upload instead.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
  };

  const uploadRecording = async () => {
    if (!audioBlob) return;
    
    // Create a file from the blob
    const file = new File([audioBlob], `recording_${Date.now()}.webm`, { 
      type: 'audio/webm' 
    });
    
    await uploadFile(file);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show recorded audio preview
  if (audioBlob && audioUrl) {
    return (
      <div className="border-2 border-dashed border-green-500 rounded-lg p-6 text-center bg-green-500/10">
        <div className="text-4xl mb-4">✅</div>
        <p className="text-gray-300 mb-4">Recording complete! ({formatTime(recordingTime)})</p>
        
        <audio src={audioUrl} controls className="w-full mb-4" />
        
        <div className="flex justify-center space-x-3">
          <button
            onClick={cancelRecording}
            className="btn btn-secondary"
            disabled={isUploading}
          >
            🗑 Discard
          </button>
          <button
            onClick={uploadRecording}
            className="btn btn-primary"
            disabled={isUploading}
          >
            {isUploading ? (
              <span className="flex items-center">
                <span className="spinner w-4 h-4 mr-2"></span>
                Uploading...
              </span>
            ) : (
              '⬆ Upload Recording'
            )}
          </button>
        </div>
      </div>
    );
  }

  // Show recording in progress
  if (isRecording) {
    return (
      <div className="border-2 border-red-500 rounded-lg p-8 text-center bg-red-500/10 animate-pulse">
        <div className="text-5xl mb-4">🔴</div>
        <p className="text-xl font-bold text-red-400 mb-2">Recording...</p>
        <p className="text-3xl font-mono mb-4">{formatTime(recordingTime)}</p>
        <p className="text-gray-400 text-sm mb-4">
          Speak clearly into your microphone
        </p>
        <button
          onClick={stopRecording}
          className="btn bg-red-600 hover:bg-red-700 text-white px-8"
        >
          ⏹ Stop Recording
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-purple-500 bg-purple-500/10'
            : 'border-gray-600 hover:border-gray-500'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {isUploading ? (
          <div className="flex flex-col items-center">
            <span className="spinner w-10 h-10 mb-4"></span>
            <p className="text-gray-400">Uploading...</p>
          </div>
        ) : (
          <>
            <div className="text-4xl mb-4">📁</div>
            <p className="text-gray-300 mb-2">
              Drag and drop audio files here
            </p>
            <p className="text-gray-500 text-sm mb-4">
              WAV, MP3, WebM - max 10MB
            </p>
            <label className="btn btn-secondary cursor-pointer">
              Choose File
              <input
                type="file"
                accept=".wav,.mp3,.webm,audio/*"
                onChange={handleChange}
                className="hidden"
              />
            </label>
          </>
        )}
      </div>

      {/* Divider */}
      <div className="flex items-center">
        <div className="flex-1 border-t border-gray-700"></div>
        <span className="px-4 text-gray-500 text-sm">or</span>
        <div className="flex-1 border-t border-gray-700"></div>
      </div>

      {/* Record Button */}
      <div className="text-center">
        <button
          onClick={startRecording}
          className="btn bg-red-600 hover:bg-red-700 text-white px-8 py-3"
          disabled={isUploading}
        >
          <span className="mr-2">🎙</span>
          Record Voice Sample
        </button>
        <p className="text-gray-500 text-sm mt-2">
          Click to record directly from your microphone
        </p>
      </div>
    </div>
  );
}

export default SampleUploader;
